// "Exif\0\0", the payload prefix that tells an APP1 segment apart from the
// other things that share its marker, XMP among them.
const EXIF_PREFIX = [0x45, 0x78, 0x69, 0x66, 0x00, 0x00];

const APP1 = 0xe1;

const START_OF_SCAN = 0xda;

const END_OF_IMAGE = 0xd9;

const ORIENTATION_TAG = 0x0112;

// Marker, two length bytes, then the "Exif\0\0" prefix, so the TIFF header the
// offsets inside the segment are measured from starts here.
const TIFF_START = 4 + EXIF_PREFIX.length;

const LITTLE_ENDIAN = 0x4949;

const BIG_ENDIAN = 0x4d4d;

/**
 * Puts the original photo's EXIF back onto a re-encoded copy, with the
 * orientation tag reset to 1.
 *
 * A canvas re-encode keeps the pixels and drops everything else, which would
 * lose the GPS position, the date the photo was taken and the camera it came
 * from - all of which the upload API reads off the file and stores.
 *
 * Resetting the orientation is what makes this safe. The canvas has already
 * turned the pixels the right way up, and the API puts every upload through
 * sharp's rotate(), so a surviving tag would turn them a second time. 1 means
 * "already upright" and leaves both sharp and the browser alone.
 *
 * Returns the re-encoded image untouched if there is nothing to carry across or
 * anything about the segment does not look right: the metadata is worth less
 * than the photo.
 */
export async function carryExif(original: File, reduced: Blob): Promise<Blob> {
  try {
    const segment = findExifSegment(new Uint8Array(await original.arrayBuffer()));

    if (!segment) {
      return reduced;
    }

    const bytes = new Uint8Array(await reduced.arrayBuffer());

    if (!startsWithMarker(bytes)) {
      return reduced;
    }

    // Straight after the SOI marker, which is where the EXIF specification puts
    // it. Nothing else has to move: the offsets inside the segment are relative
    // to its own TIFF header, and rewriting the orientation changes a value in
    // place rather than resizing anything.
    return new Blob([bytes.subarray(0, 2), uprightOrientation(segment), bytes.subarray(2)], {
      type: reduced.type,
    });
  } catch (error) {
    console.error(error);

    return reduced;
  }
}

function startsWithMarker(bytes: Uint8Array) {
  return bytes[0] === 0xff && bytes[1] === 0xd8;
}

// Walks the marker segments between the SOI and the start of the image data.
function findExifSegment(bytes: Uint8Array): Uint8Array | null {
  if (!startsWithMarker(bytes)) {
    return null;
  }

  let offset = 2;

  while (offset + 4 <= bytes.length) {
    if (bytes[offset] !== 0xff) {
      return null;
    }

    const marker = bytes[offset + 1] as number;

    // Padding between segments is legal and written as repeated 0xff bytes.
    if (marker === 0xff) {
      offset += 1;
      continue;
    }

    // The compressed data starts here, so there are no more headers to read.
    if (marker === START_OF_SCAN || marker === END_OF_IMAGE) {
      return null;
    }

    const length = ((bytes[offset + 2] as number) << 8) | (bytes[offset + 3] as number);

    // A segment has to be long enough to hold its own length.
    if (length < 2 || offset + 2 + length > bytes.length) {
      return null;
    }

    if (marker === APP1) {
      const payload = bytes.subarray(offset + 4, offset + 2 + length);

      if (EXIF_PREFIX.every((byte, index) => payload[index] === byte)) {
        return bytes.subarray(offset, offset + 2 + length);
      }
    }

    offset += 2 + length;
  }

  return null;
}

// Copied rather than written through, so the caller's original file is left as
// it was. A DataView throws on a read past the end, which the caller treats as
// a reason to skip the metadata rather than a reason to lose the photo.
function uprightOrientation(segment: Uint8Array): Uint8Array {
  const copy = new Uint8Array(segment);
  const view = new DataView(copy.buffer);
  const byteOrder = view.getUint16(TIFF_START);

  if (byteOrder !== LITTLE_ENDIAN && byteOrder !== BIG_ENDIAN) {
    return copy;
  }

  const littleEndian = byteOrder === LITTLE_ENDIAN;
  const ifd0 = TIFF_START + view.getUint32(TIFF_START + 4, littleEndian);
  const entries = view.getUint16(ifd0, littleEndian);

  for (let entry = 0; entry < entries; entry++) {
    // Each entry is a tag, a format, a count and then four bytes of value.
    const field = ifd0 + 2 + entry * 12;

    if (view.getUint16(field, littleEndian) === ORIENTATION_TAG) {
      view.setUint16(field + 8, 1, littleEndian);

      return copy;
    }
  }

  return copy;
}
